import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import { EXCHANGES, DEFAULT_SELECTED } from './constants/exchanges';
import AdminPage from './components/AdminPage';
import HistoryPage from './components/HistoryPage';
import HedgingStatus from './components/HedgingStatus';
import MarginTable from './components/MarginTable';
import PositionsTable from './components/PositionsTable';
import { useFundingRateWebSocket } from './hooks/useFundingRateWebSocket';
import { useDashboardData } from './hooks/useDashboardData';
import { useIsMobile } from './hooks/useIsMobile';
import { getExtendedStats } from './utils/stats';
import MobileNavigation from './components/MobileNavigation';
import MobileOverview from './components/MobileOverview';
import MobilePositions from './components/MobilePositions';
import MobileHedging from './components/MobileHedging';
import MobileSettings from './components/MobileSettings';
import AccountSelector from './components/AccountSelector';
import Login from './components/Login';
import SummaryCard from './components/SummaryCard';
import LoadingView from './components/LoadingView';
import ErrorView from './components/ErrorView';
import DocsPage from './components/DocsPage';
import { useAuth } from './contexts/AuthContext';

function App() {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const currentTab = pathname === '/docs' ? 'docs' : pathname === '/funding' ? 'history' : pathname === '/admin' ? 'admin' : 'dashboard';

  const [selectedExchanges, setSelectedExchanges] = useState(() => {
    const saved = localStorage.getItem('selectedExchanges');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {}
    }
    return { ...DEFAULT_SELECTED };
  });

  const [isPaused, setIsPaused] = useState(() => localStorage.getItem('autoUpdatePaused') === 'true');
  const historyRefreshRef = useRef(null);
  const [adminSection, setAdminSection] = useState('settings');
  const isMobile = useIsMobile();
  const [activeMobileView, setActiveMobileView] = useState('overview');

  const { rates: liveFundingRates, isConnected, error: wsError } = useFundingRateWebSocket(true);
  const [lastUpdateFlash, setLastUpdateFlash] = useState(false);
  const [lastUpdatedTime, setLastUpdatedTime] = useState(null);

  const dashboard = useDashboardData({
    currentTab,
    isPaused,
    selectedExchanges,
    auth,
    liveFundingRates,
    isConnected,
  });

  const {
    positionsData: enrichedPositionsData,
    hedgingAnalysis,
    fundingNetByMarket,
    systemMetrics,
    isLoading,
    error,
    manualRefresh: dashboardManualRefresh,
  } = dashboard;

  const wsStatus = isConnected ? 'connected' : (wsError ? 'error' : 'disconnected');

  useEffect(() => {
    localStorage.setItem('autoUpdatePaused', String(isPaused));
  }, [isPaused]);

  const togglePause = () => setIsPaused((prev) => !prev);

  const manualRefresh = () => {
    if (currentTab === 'history' && historyRefreshRef.current) {
      historyRefreshRef.current();
      return;
    }
    dashboardManualRefresh();
  };

  useEffect(() => {
    localStorage.setItem('selectedExchanges', JSON.stringify(selectedExchanges));
  }, [selectedExchanges]);

  const toggleExchange = (exchange) => {
    setSelectedExchanges((prev) => ({ ...prev, [exchange]: !prev[exchange] }));
  };

  useEffect(() => {
    if (enrichedPositionsData?.last_updated) {
      const newTime = enrichedPositionsData.last_updated;
      if (newTime !== lastUpdatedTime) {
        setLastUpdatedTime(newTime);
        setLastUpdateFlash(true);
        setTimeout(() => setLastUpdateFlash(false), 500);
      }
    }
  }, [enrichedPositionsData?.last_updated, lastUpdatedTime]);

  const hasAnyPositions = useMemo(
    () =>
      EXCHANGES.some((ex) => {
        const pos = enrichedPositionsData?.[ex]?.positions;
        return Array.isArray(pos) && pos.length > 0;
      }),
    [enrichedPositionsData]
  );

  // Login gate: when Firebase is configured, require sign-in (after all hooks)
  if (auth?.isFirebaseConfigured && !auth?.user) {
    return <Login />;
  }

  if (isLoading && currentTab !== 'history') {
    return <LoadingView mobile={isMobile} />;
  }

  if (error) {
    console.error('Frontend error details:', error);
    return (
      <ErrorView
        message={error.message}
        onRetry={manualRefresh}
        error={error}
        mobile={isMobile}
      />
    );
  }

  // Determine connection status dot color
  const getConnectionDotColor = () => {
    if (wsStatus === 'connected') return 'green';
    if (wsStatus === 'error') return 'red';
    if (wsStatus === 'disconnected') return 'orange'; // Orange for disconnected (will reconnect)
    return 'orange'; // connecting
  };

  // Mobile view
  if (isMobile) {
    // On mobile, /funding, /admin, /docs show full-page content; otherwise dashboard sub-views
    if (pathname === '/docs') {
      return (
        <div className="App mobile-app">
          <div className="mobile-content-wrapper">
            <DocsPage />
            <p style={{ padding: '12px', textAlign: 'center' }}>
              <button type="button" onClick={() => navigate('/')} className="control-button">
                ← Dashboard
              </button>
            </p>
          </div>
        </div>
      );
    }
    if (pathname === '/funding') {
      const handleMobileNav = (tabId) => {
        if (tabId === 'funding') navigate('/funding');
        else {
          navigate('/');
          setActiveMobileView(tabId);
        }
      };
      return (
        <div className="App mobile-app">
          <div className="mobile-content-wrapper">
            <HistoryPage onRefreshRef={historyRefreshRef} positions={enrichedPositionsData} />
          </div>
          <MobileNavigation activeView="funding" onViewChange={handleMobileNav} />
        </div>
      );
    }
    if (pathname === '/admin') {
      return (
        <div className="App mobile-app">
          <div className="mobile-content-wrapper">
            <AdminPage section={adminSection} onSectionChange={setAdminSection} />
            <p style={{ padding: '12px', textAlign: 'center' }}>
              <button type="button" onClick={() => navigate('/')} className="control-button">
                ← Dashboard
              </button>
            </p>
          </div>
        </div>
      );
    }

    const handleMobileNav = (tabId) => {
      if (tabId === 'funding') navigate('/funding');
      else setActiveMobileView(tabId);
    };

    return (
      <div className="App mobile-app">
        <div className="mobile-content-wrapper">
          {activeMobileView === 'overview' && (
            <MobileOverview 
              positions={enrichedPositionsData} 
              selectedExchanges={selectedExchanges}
            />
          )}
          {activeMobileView === 'positions' && (
            <MobilePositions 
              positions={enrichedPositionsData}
              selectedExchanges={selectedExchanges}
              liveFundingRates={liveFundingRates}
            />
          )}
          {activeMobileView === 'hedging' && (
            <MobileHedging 
              hedgingAnalysis={hedgingAnalysis}
            />
          )}
          {activeMobileView === 'settings' && (
            <MobileSettings 
              selectedExchanges={selectedExchanges}
              onExchangeToggle={toggleExchange}
              accounts={auth?.accounts || []}
              selectedAccountId={auth?.selectedAccountId}
              onSelectAccount={auth?.setSelectedAccountId}
              accountsLoading={auth?.accountsLoading}
              onManageAccount={() => window.open('https://funds.frasyndicate.com/admin/account', '_blank')}
              onLogout={() => {
                if (auth?.logout) {
                  auth.logout();
                } else {
                  localStorage.clear();
                  window.location.reload();
                }
              }}
            />
          )}
        </div>
        <MobileNavigation 
          activeView={activeMobileView}
          onViewChange={handleMobileNav}
        />
      </div>
    );
  }

  // Desktop view (layout per docs/FRONTEND_SHARED_UI_GUIDE.md)
  return (
    <div className="App">
      <header className="App-header">
        <div className="header-content">
          <h1 className="header-title">Funding Rate Monitor</h1>
          <div className="header-right desktop-only">
            <nav className="nav-tabs">
              <button
                type="button"
                className={`nav-tab ${currentTab === 'dashboard' ? 'active' : ''}`}
                onClick={() => navigate('/')}
              >
                Dashboard
              </button>
              <button
                type="button"
                className={`nav-tab ${currentTab === 'history' ? 'active' : ''}`}
                onClick={() => navigate('/funding')}
              >
                Funding
              </button>
              <button
                type="button"
                className={`nav-tab ${currentTab === 'admin' ? 'active' : ''}`}
                onClick={() => navigate('/admin')}
              >
                Admin
              </button>
            </nav>
            <div className="connection-status">
              <span className={`status-dot ${getConnectionDotColor()}`} aria-hidden />
              <span className="status-text">{wsStatus === 'connected' ? 'Connected' : wsStatus === 'error' ? 'Error' : 'Disconnected'}</span>
            </div>
            {!auth?.isFirebaseConfigured && (
              <span className="header-login-hint" title="Set REACT_APP_FIREBASE_API_KEY, REACT_APP_FIREBASE_AUTH_DOMAIN, REACT_APP_FIREBASE_PROJECT_ID in .env to enable login and logout">
                Login (set Firebase env to enable)
              </span>
            )}
            {auth?.user && (
              <>
                {((auth.accounts || []).length > 0) && (
                  <AccountSelector
                    accounts={auth.accounts || []}
                    selectedAccountId={auth.selectedAccountId}
                    onSelect={auth.setSelectedAccountId}
                    onManageAccount={() => {
                      navigate('/admin');
                      setAdminSection('manage-account');
                    }}
                    accountsLoading={auth.accountsLoading}
                    accountsError={auth.accountsError}
                  />
                )}
                <div className="user-menu">
                  <span className="user-email">{auth.user?.email || ''}</span>
                  <button type="button" className="logout-button" onClick={() => auth.logout()}>
                    Logout
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Page toolbar: exchange picker + controls (dashboard only) */}
      {currentTab === 'dashboard' && (
        <div className="page-toolbar">
          <div className="exchange-selector">
            <span className="exchange-selector-label">Exchanges:</span>
            <div className="exchange-buttons">
              {EXCHANGES.map((ex) => (
                <button
                  key={ex}
                  type="button"
                  className={`exchange-button ${selectedExchanges[ex] ? 'active' : ''}`}
                  onClick={() => toggleExchange(ex)}
                  title={selectedExchanges[ex] ? `Disable ${ex.charAt(0).toUpperCase() + ex.slice(1)}` : `Enable ${ex.charAt(0).toUpperCase() + ex.slice(1)}`}
                >
                  {ex.charAt(0).toUpperCase() + ex.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <div className="page-toolbar-controls">
            <button
              className={`control-button ${isPaused ? 'paused' : 'active'}`}
              onClick={togglePause}
              title={isPaused ? 'Resume auto-updates' : 'Pause auto-updates'}
            >
              {isPaused ? '▶' : '⏸'}
            </button>
            <button
              className="control-button refresh-button"
              onClick={manualRefresh}
              title="Manual refresh"
            >
              ↻
            </button>
            <span className={`last-updated ${lastUpdateFlash ? 'flash' : ''}`}>
              Last updated: {enrichedPositionsData?.last_updated ?
                new Date(enrichedPositionsData.last_updated).toLocaleString() :
                (enrichedPositionsData ? 'Just now' : 'N/A')}
              {isPaused && <span className="paused-indicator"> (Paused)</span>}
            </span>
          </div>
        </div>
      )}

      <main className="main-content">
        <div className="dashboard">
          {currentTab === 'docs' && <DocsPage />}
          {currentTab === 'admin' && (
            <AdminPage
              section={adminSection}
              onSectionChange={setAdminSection}
            />
          )}
          {currentTab === 'history' && <HistoryPage onRefreshRef={historyRefreshRef} positions={enrichedPositionsData} />}
          {currentTab === 'dashboard' && (
          <>
        {/* All Positions Table View */}
        <PositionsTable positions={enrichedPositionsData} hedgingAnalysis={hedgingAnalysis} fundingNetByMarket={fundingNetByMarket} />
        
        {/* Margin Overview Table */}
        <MarginTable positions={enrichedPositionsData} />
        
        {/* Summary Cards */}
        <section className="balances-section">
          <h2>Balances</h2>
          {!hasAnyPositions ? (
            <div className="no-positions">
              <p>No exchange data to display.</p>
            </div>
          ) : (
            <div className="summary-section">
              {EXCHANGES.filter((ex) => selectedExchanges[ex]).map((ex) => {
                const positions = (enrichedPositionsData?.[ex]?.positions && Array.isArray(enrichedPositionsData[ex].positions))
                  ? enrichedPositionsData[ex].positions
                  : [];
                const exchangeData = enrichedPositionsData?.[ex];
                const stats = getExtendedStats(positions);
                return (
                  <SummaryCard
                    key={ex}
                    exchangeLabel={ex.charAt(0).toUpperCase() + ex.slice(1)}
                    positions={positions}
                    exchangeData={exchangeData}
                    stats={stats}
                  />
                );
              })}
            </div>
          )}
        </section>

        {/* Hedging Analysis */}
        {hedgingAnalysis.length > 0 && (
          <section className="hedging-section">
            <h2>Hedging Analysis</h2>
            <div className="hedging-grid">
              {hedgingAnalysis.map((analysis, idx) => (
                <HedgingStatus key={idx} analysis={analysis} />
              ))}
            </div>
          </section>
        )}

        {/* No Positions Message */}
        {!hasAnyPositions && (
          <div className="no-positions">
            <p>No open positions detected.</p>
          </div>
        )}
          </>
        )}
        </div>
      </main>

      {/* Footer with app name, Docs link, API status, and system metrics */}
      <footer className="App-footer">
        <div className="footer-row">
          <span className="footer-brand">
            Funding Rate Monitor
            <span className="footer-sep"> · </span>
            <button
              type="button"
              className="footer-docs-link"
              onClick={() => navigate('/docs')}
            >
              Docs
            </button>
            <span className="footer-sep"> · </span>
            <span className="footer-api-status">API: {wsStatus === 'connected' ? 'Connected' : wsStatus === 'error' ? 'Error' : 'Disconnected'}</span>
          </span>
          {systemMetrics && (
            <div className="footer-metrics">
              <span className="metric-item" title="System CPU Usage">
                CPU: {systemMetrics.cpu_percent?.toFixed(1) || '0.0'}%
              </span>
              <span className="metric-item" title={`System Memory: ${systemMetrics.memory_used_mb?.toFixed(0) || 0} MB / ${systemMetrics.memory_total_mb?.toFixed(0) || 0} MB`}>
                RAM: {systemMetrics.memory_percent?.toFixed(1) || '0.0'}% ({systemMetrics.memory_used_mb?.toFixed(0) || '0'} MB)
              </span>
              <span className="metric-item" title={`Process Memory: ${systemMetrics.process_memory_mb?.toFixed(0) || '0'} MB (${systemMetrics.process_memory_percent?.toFixed(2) || '0.0'}% of system)`}>
                Process: {systemMetrics.process_memory_mb?.toFixed(0) || '0'} MB
              </span>
              {systemMetrics.binance_rate_limit?.is_rate_limited && (
                <span className="metric-item rate-limited" title={`Binance rate limited until ${systemMetrics.binance_rate_limit.rate_limit_until || 'unknown'}`}>
                  Binance RL: {systemMetrics.binance_rate_limit.seconds_remaining || 0}s
                </span>
              )}
            </div>
          )}
        </div>
      </footer>
    </div>
  );
}

export default App;

